/**
 * Merkezi RBAC (Role-Based Access Control) Matrisi
 * Frontend & Backend tarafında kullanılan tüm izinler burada tanımlanır
 * Single Source of Truth ilkesini sağlar
 */

export type Resource =
  // Dashboard
  | 'dashboard'
  // Araç Gereç
  | 'companies'
  | 'locations'
  | 'suppliers'
  | 'equipment'
  | 'consumables'
  | 'documents'
  | 'maintenance-contracts'
  | 'damage-theft'
  // Siparişler & Capex
  | 'orders'
  | 'capex'
  // Yazıcılar
  | 'printers'
  | 'printer-drivers'
  // Teknik Servis
  | 'technical-service'
  // GSM & Telefon
  | 'gsm-lines'
  | 'mobile-phones'
  | 'brand-model-management'
  | 'tariffs'
  | 'phone-replacements'
  // Sunucular
  | 'physical-servers'
  | 'virtual-servers'
  // Industrial
  | 'iot-devices'
  | 'scales'
  | 'radios'
  | 'handheld-terminals'
  // Ağ
  | 'network-switches'
  | 'cabinets'
  | 'circuits'
  | 'vlans'
  // Storage
  | 'storage-units'
  // Toplantılar
  | 'meetings'
  // Zimmetleme
  | 'assignment-forms'
  | 'assignment-logs'
  | 'assignment-definitions'
  // Güvenlik Sistemleri
  | 'cameras'
  | 'camera-faults'
  | 'nvrs'
  // Ayarlar
  | 'system-settings'
  | 'users'
  | 'user-activity'
  | 'announcements'
  | 'menu-management'
  | 'role-permissions'
  // Cargo
  | 'cargo-labels'
  // Diğer
  | 'activities'
  | 'assignments'
  | 'tasks'
  | 'notifications';

export type Action = 'view' | 'create' | 'edit' | 'delete' | 'manage' | 'export' | 'import';

export type Role = 'superadmin' | 'admin' | 'teknisyen' | 'calisan';

interface ResourcePermission {
  actions: Action[];
  description?: string;
}

interface RolePermissions {
  resources: {
    [K in Resource]?: ResourcePermission;
  };
}

/**
 * RBAC Matrisi - Her rol için hangi resource'lara ne yapabilir?
 * En kısıtlı (calisan) -> En açık (superadmin)
 */
export const RBAC_MATRIX: Record<Role, RolePermissions> = {
  // ============================================
  // ÇALIŞAN - En az yetki
  // ============================================
  calisan: {
    resources: {
      dashboard: { actions: ['view'], description: 'Dashboard görüntüleme' },
      'technical-service': {
        actions: ['view', 'create'],
        description: 'Teknik servis talebini açabilir',
      },
      'user-activity': { actions: ['view'], description: 'Kendi aktiviteleri' },
      profile: { actions: ['view', 'edit'], description: 'Kendi profili' },
      'notification-settings': {
        actions: ['view', 'edit'],
        description: 'Bildirim ayarları',
      },
    },
  },

  // ============================================
  // TEKNİSYEN
  // ============================================
  teknisyen: {
    resources: {
      dashboard: { actions: ['view'] },
      companies: { actions: ['view'] },
      locations: { actions: ['view'] },
      suppliers: { actions: ['view', 'create', 'edit'] },
      equipment: { actions: ['view'] },
      consumables: { actions: ['view'] },
      'technical-service': {
        actions: ['view', 'create', 'edit', 'delete'],
        description: 'Teknik servisleri yönet',
      },
      'camera-faults': { actions: ['view', 'create', 'edit'] },
      cameras: { actions: ['view', 'edit'] },
      nvrs: { actions: ['view', 'edit'] },
      'iot-devices': { actions: ['view', 'edit'] },
      scales: { actions: ['view', 'edit'] },
      radios: { actions: ['view', 'create', 'edit', 'delete'] },
      'handheld-terminals': { actions: ['view', 'edit'] },
      'network-switches': { actions: ['view', 'edit'] },
      cabinets: { actions: ['view', 'edit'] },
      circuits: { actions: ['view', 'edit'] },
      'activities': { actions: ['view'] },
      'assignments': { actions: ['view'] },
    },
  },

  // ============================================
  // ADMIN - Çoğu yetki
  // ============================================
  admin: {
    resources: {
      dashboard: { actions: ['view'] },
      companies: {
        actions: ['view', 'create', 'edit', 'delete'],
        description: 'Şirketleri yönet',
      },
      locations: {
        actions: ['view', 'create', 'edit', 'delete'],
        description: 'Lokasyonları yönet',
      },
      suppliers: {
        actions: ['view', 'create', 'edit', 'delete'],
        description: 'Tedarikçileri yönet',
      },
      equipment: { actions: ['view', 'create', 'edit', 'delete'] },
      consumables: { actions: ['view', 'create', 'edit', 'delete'] },
      documents: { actions: ['view', 'create', 'edit', 'delete'] },
      'maintenance-contracts': { actions: ['view', 'create', 'edit', 'delete'] },
      'damage-theft': { actions: ['view', 'create', 'edit', 'delete'] },
      orders: { actions: ['view', 'create', 'edit', 'delete', 'export'] },
      capex: { actions: ['view', 'create', 'edit', 'delete'] },
      printers: { actions: ['view', 'create', 'edit', 'delete'] },
      'printer-drivers': { actions: ['view', 'create', 'edit', 'delete'] },
      'technical-service': { actions: ['view', 'create', 'edit', 'delete'] },
      'gsm-lines': { actions: ['view', 'create', 'edit', 'delete'] },
      'mobile-phones': { actions: ['view', 'create', 'edit', 'delete'] },
      'brand-model-management': { actions: ['view', 'create', 'edit', 'delete'] },
      tariffs: { actions: ['view', 'create', 'edit', 'delete'] },
      'phone-replacements': { actions: ['view', 'create', 'edit', 'delete'] },
      'physical-servers': { actions: ['view', 'create', 'edit', 'delete'] },
      'virtual-servers': { actions: ['view', 'create', 'edit', 'delete'] },
      'iot-devices': { actions: ['view', 'create', 'edit', 'delete'] },
      scales: { actions: ['view', 'create', 'edit', 'delete'] },
      radios: { actions: ['view', 'create', 'edit', 'delete'] },
      'handheld-terminals': { actions: ['view', 'create', 'edit', 'delete'] },
      'network-switches': { actions: ['view', 'create', 'edit', 'delete'] },
      cabinets: { actions: ['view', 'create', 'edit', 'delete'] },
      circuits: { actions: ['view', 'create', 'edit', 'delete'] },
      'storage-units': { actions: ['view', 'create', 'edit', 'delete'] },
      meetings: { actions: ['view', 'create', 'edit', 'delete'] },
      'assignment-forms': { actions: ['view', 'create', 'edit', 'delete'] },
      'assignment-logs': { actions: ['view', 'create', 'edit', 'delete'] },
      'assignment-definitions': { actions: ['view', 'create', 'edit', 'delete'] },
      cameras: { actions: ['view', 'create', 'edit', 'delete'] },
      'camera-faults': { actions: ['view', 'create', 'edit', 'delete'] },
      nvrs: { actions: ['view', 'create', 'edit', 'delete'] },
      users: { actions: ['view', 'edit'], description: 'Kullanıcıları düzenle' },
      'user-activity': { actions: ['view'] },
      'menu-management': { actions: ['view'] },
      'cargo-labels': { actions: ['view', 'create', 'edit', 'delete'] },
      'activities': { actions: ['view'] },
      'assignments': { actions: ['view', 'create', 'edit', 'delete'] },
    },
  },

  // ============================================
  // SUPERADMIN - Tüm yetkiler
  // ============================================
  superadmin: {
    resources: {
      dashboard: { actions: ['view'] },
      companies: { actions: ['view', 'create', 'edit', 'delete'] },
      locations: { actions: ['view', 'create', 'edit', 'delete'] },
      suppliers: { actions: ['view', 'create', 'edit', 'delete'] },
      equipment: { actions: ['view', 'create', 'edit', 'delete'] },
      consumables: { actions: ['view', 'create', 'edit', 'delete'] },
      documents: { actions: ['view', 'create', 'edit', 'delete'] },
      'maintenance-contracts': { actions: ['view', 'create', 'edit', 'delete'] },
      'damage-theft': { actions: ['view', 'create', 'edit', 'delete'] },
      orders: { actions: ['view', 'create', 'edit', 'delete', 'export', 'import'] },
      capex: { actions: ['view', 'create', 'edit', 'delete'] },
      printers: { actions: ['view', 'create', 'edit', 'delete'] },
      'printer-drivers': { actions: ['view', 'create', 'edit', 'delete'] },
      'technical-service': { actions: ['view', 'create', 'edit', 'delete'] },
      'gsm-lines': { actions: ['view', 'create', 'edit', 'delete'] },
      'mobile-phones': { actions: ['view', 'create', 'edit', 'delete'] },
      'brand-model-management': { actions: ['view', 'create', 'edit', 'delete'] },
      tariffs: { actions: ['view', 'create', 'edit', 'delete'] },
      'phone-replacements': { actions: ['view', 'create', 'edit', 'delete'] },
      'physical-servers': { actions: ['view', 'create', 'edit', 'delete'] },
      'virtual-servers': { actions: ['view', 'create', 'edit', 'delete'] },
      'iot-devices': { actions: ['view', 'create', 'edit', 'delete'] },
      scales: { actions: ['view', 'create', 'edit', 'delete'] },
      radios: { actions: ['view', 'create', 'edit', 'delete'] },
      'handheld-terminals': { actions: ['view', 'create', 'edit', 'delete'] },
      'network-switches': { actions: ['view', 'create', 'edit', 'delete'] },
      cabinets: { actions: ['view', 'create', 'edit', 'delete'] },
      circuits: { actions: ['view', 'create', 'edit', 'delete'] },
      'storage-units': { actions: ['view', 'create', 'edit', 'delete'] },
      meetings: { actions: ['view', 'create', 'edit', 'delete'] },
      'assignment-forms': { actions: ['view', 'create', 'edit', 'delete'] },
      'assignment-logs': { actions: ['view', 'create', 'edit', 'delete'] },
      'assignment-definitions': { actions: ['view', 'create', 'edit', 'delete'] },
      cameras: { actions: ['view', 'create', 'edit', 'delete'] },
      'camera-faults': { actions: ['view', 'create', 'edit', 'delete'] },
      nvrs: { actions: ['view', 'create', 'edit', 'delete'] },
      'system-settings': { actions: ['view', 'manage'] },
      users: { actions: ['view', 'create', 'edit', 'delete', 'manage'] },
      'user-activity': { actions: ['view', 'manage'] },
      announcements: { actions: ['view', 'create', 'edit', 'delete'] },
      'menu-management': { actions: ['view', 'manage'] },
      'role-permissions': { actions: ['view', 'manage'] },
      'cargo-labels': { actions: ['view', 'create', 'edit', 'delete'] },
      'activities': { actions: ['view', 'manage'] },
      'assignments': { actions: ['view', 'create', 'edit', 'delete'] },
      'tasks': { actions: ['view', 'create', 'edit', 'delete'] },
      'notifications': { actions: ['view', 'manage'] },
    },
  },
};

/**
 * Bir role'ün belirli resource'a hangi action'lara sahip olduğunu kontrol et
 */
export const hasPermission = (
  role: Role,
  resource: Resource,
  action: Action = 'view'
): boolean => {
  const rolePerms = RBAC_MATRIX[role];
  if (!rolePerms) return false;

  const resourcePerms = rolePerms.resources[resource];
  if (!resourcePerms) return false;

  return resourcePerms.actions.includes(action);
};

/**
 * Bir role'ün erişebildiği tüm resource'ları döndür
 */
export const getAccessibleResources = (role: Role): Resource[] => {
  const rolePerms = RBAC_MATRIX[role];
  if (!rolePerms) return [];

  return Object.keys(rolePerms.resources) as Resource[];
};

/**
 * Tüm rolleri döndür
 */
export const getAllRoles = (): Role[] => {
  return Object.keys(RBAC_MATRIX) as Role[];
};

/**
 * Resource'un description'ını döndür
 */
export const getResourceDescription = (resource: Resource, role: Role): string | undefined => {
  const rolePerms = RBAC_MATRIX[role];
  if (!rolePerms) return undefined;

  return rolePerms.resources[resource]?.description;
};

/**
 * Bir role'ün belirli resource için hangi action'ları yaptığını döndür
 */
export const getResourceActions = (role: Role, resource: Resource): Action[] => {
  const rolePerms = RBAC_MATRIX[role];
  if (!rolePerms) return [];

  return rolePerms.resources[resource]?.actions || [];
};

/**
 * Frontend'de profil sayfası gibi özel kullanımlar için
 * Kullanıcı kendi profilini her zaman düzenleyebilir
 */
export const canEditOwnProfile = (role: Role): boolean => {
  return true; // Tüm roller kendi profil'ini düzenleyebilir
};
